import {
  DocumentServicesObject,
  CompStructure,
  AppDataComponent,
  AppComponent,
  PresetsValue,
} from '@wix/document-services-types';
import { WidgetPointer } from '@wix/editor-platform-host-integration-apis';
import { ComponentTypes } from '@wix/editor-const';

import { addIf } from '../../../../utils';

import {
  BLOCKS_WIDGET,
  CE_WIDGET,
  IFRAME_PAGE,
  IFRAME_WIDGET,
  OOI_PAGE,
  OOI_WIDGET,
  EXPERIMENT_SE_WP_RESP_CONTAINER_LAYOUT,
  BUILDER_WIDGET,
} from '../constants';
import { isBuilderComponentModelSupported } from '../../../bootstrap/platformApiEnhancer/builderComponentModel';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';

const SUPPORTED_WIDGETS = [
  IFRAME_WIDGET,
  IFRAME_PAGE,
  OOI_WIDGET,
  OOI_PAGE,
  BLOCKS_WIDGET,
  CE_WIDGET,
  BUILDER_WIDGET,
] as const;

export type SupportedWidgetType = (typeof SUPPORTED_WIDGETS)[number];

type CompDefCreator = (
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
  applicationId: number,
  widgetPluginAppComponent: AppComponent,
  experimentsAPI: IExperimentsApiAdapter,
  componentTypeOverride?: string,
) => CompStructure | Promise<CompStructure>;

const compDefCreators: Record<SupportedWidgetType, CompDefCreator> = {
  [IFRAME_WIDGET]: createIFrameComponent,
  [IFRAME_PAGE]: createIFrameComponent,
  [OOI_WIDGET]: createTPAWidgetComponent,
  [OOI_PAGE]: createTPAWidgetComponent,
  [BLOCKS_WIDGET]: createBlocksWidgetComponent,
  [CE_WIDGET]: createCustomElementWidgetComponent,
  [BUILDER_WIDGET]: createBuilderWidgetComponent,
};
interface LayoutOptions {
  experimentsAPI: IExperimentsApiAdapter;
  height?: number;
  componentType?: string;
}

interface CreateWidgetPluginComponentDefinitionOptions {
  definitionOverrides?: (componentDefinition: CompStructure) => CompStructure;
}

async function getDefaultPreset(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
) {
  const appDescriptor =
    await documentServicesAPI.appStudioWidgets.getAppDescriptor(
      widgetPointer.appDefinitionId,
    );

  const widgetDescriptor = Object.values(appDescriptor.widgets).find(
    (widget) => widget.devCenterWidgetId === widgetPointer.widgetId,
  );

  const [defaultPreset] = widgetDescriptor?.presets ?? [];

  if (!defaultPreset) {
    return undefined;
  }

  const presetId = defaultPreset.presetId.replace('#', '');

  return {
    type: 'PresetData',
    layout: presetId,
    style: presetId,
  };
}

function createBuilderWidgetComponent(
  documentServicesAPI: DocumentServicesObject,
  _widgetPointer: WidgetPointer,
  _applicationId: number,
  _widgetPluginAppComponent: AppComponent,
  experimentsAPI: IExperimentsApiAdapter,
  componentType?: string,
): CompStructure {
  if (!isBuilderComponentModelSupported()) {
    throw new Error('Builder widget requires builder component model support');
  }

  if (!componentType) {
    throw new Error('Builder widget is missing component type in manifest');
  }

  const widgetComponent =
    documentServicesAPI.components.buildDefaultComponentStructure(
      componentType,
    );

  widgetComponent.layouts = {
    ...widgetComponent.layouts,
    ...getResponsiveLayouts({
      componentType,
      experimentsAPI,
    }),
  } as any;

  return widgetComponent;
}

async function createBlocksWidgetComponent(
  documentServicesAPI: DocumentServicesObject,
  { appDefinitionId, widgetId }: WidgetPointer,
  _applicationId: number,
  _widgetPluginAppComponent: AppComponent,
  experimentsAPI: IExperimentsApiAdapter,
  componentType: string = ComponentTypes.RefComponent,
): Promise<CompStructure> {
  const widgetComponent =
    documentServicesAPI.components.buildDefaultComponentStructure(
      componentType,
    );

  widgetComponent.data = {
    ...widgetComponent.data,
    type: 'WidgetRef',
    appDefinitionId,
    widgetId,
  };
  widgetComponent.layouts = {
    ...widgetComponent.layouts,
    ...getResponsiveLayouts({ experimentsAPI }),
  } as any;

  widgetComponent.presets = (await getDefaultPreset(documentServicesAPI, {
    appDefinitionId,
    widgetId,
  })) as PresetsValue;

  return widgetComponent;
}
async function createCustomElementWidgetComponent(
  documentServicesAPI: DocumentServicesObject,
  { appDefinitionId, widgetId }: WidgetPointer,
  _applicationId: number,
  _widgetPluginAppComponent: AppComponent,
  experimentsAPI: IExperimentsApiAdapter,
  componentType: string = ComponentTypes.CustomElement,
): Promise<CompStructure> {
  const widgetComponent =
    documentServicesAPI.components.buildDefaultComponentStructure(
      componentType,
    );

  widgetComponent.data = {
    ...widgetComponent.data,
    type: 'CustomElement',
    appDefinitionId,
    shouldReload: false,
    widgetId,
  };

  widgetComponent.layouts = {
    ...widgetComponent.layouts,
    ...getResponsiveLayouts({ experimentsAPI }),
  } as any;

  return widgetComponent;
}

function getWidgetAppComponent(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
): AppDataComponent {
  const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
    widgetPointer.appDefinitionId,
  );

  if (!appData) {
    throw new Error(
      `Application with appDefinitionId: ${widgetPointer.appDefinitionId} is missing`,
    );
  }
  const widgetAppComponent = appData.components?.find(
    (appComponent) => appComponent.componentId === widgetPointer.widgetId,
  );

  if (!widgetAppComponent) {
    throw new Error(
      `Application "${appData.name}" is missing the widget component with ID: ${widgetPointer.widgetId}`,
    );
  }

  return widgetAppComponent;
}

function createIFrameComponent(
  documentServicesAPI: DocumentServicesObject,
  { appDefinitionId, widgetId }: WidgetPointer,
  applicationId: number,
  widgetPluginAppComponent: AppComponent,
  experimentsAPI: IExperimentsApiAdapter,
  componentType: string = ComponentTypes.TpaWidget,
): CompStructure {
  const DEFAULT_INITIAL_HEIGHT = 100;

  const widgetComponent =
    documentServicesAPI.components.buildDefaultComponentStructure(
      componentType,
    );

  widgetComponent.data = {
    ...widgetComponent.data,
    appDefinitionId,
    applicationId: applicationId.toString(),
    widgetId,
  };

  // iFrame needs an initial height to be rendered. Later it can set its own height via Js-SDK
  const initialHeight =
    // @ts-expect-error
    widgetPluginAppComponent?.data?.height || DEFAULT_INITIAL_HEIGHT;

  widgetComponent.layouts = {
    ...widgetComponent.layouts,
    ...getResponsiveLayouts({
      height: initialHeight,
      componentType,
      experimentsAPI,
    }),
  } as any;

  return widgetComponent;
}

function createTPAWidgetComponent(
  documentServicesAPI: DocumentServicesObject,
  { appDefinitionId, widgetId }: WidgetPointer,
  applicationId: number,
  _widgetPluginAppComponent: AppComponent,
  experimentsAPI: IExperimentsApiAdapter,
  componentType: string = ComponentTypes.TpaWidget,
): CompStructure {
  const widgetComponent =
    documentServicesAPI.components.buildDefaultComponentStructure(
      componentType,
    );

  widgetComponent.data = {
    ...widgetComponent.data,
    appDefinitionId,
    applicationId: applicationId.toString(),
    widgetId,
  };
  widgetComponent.layouts = {
    ...widgetComponent.layouts,
    ...getResponsiveLayouts({
      componentType,
      experimentsAPI,
    }),
  } as any;

  return widgetComponent;
}

export function getWidgetPluginType(widgetAppComponent: AppDataComponent) {
  const widgetPluginType = widgetAppComponent?.type;

  if (!SUPPORTED_WIDGETS.includes(widgetPluginType as any)) {
    throw new Error(`Widget-plugin type is unsupported -- ${widgetPluginType}`);
  }

  return widgetPluginType as (typeof SUPPORTED_WIDGETS)[number];
}

export function getResponsiveLayouts(options: LayoutOptions) {
  const shouldAddContainerLayout = options.experimentsAPI?.enabled(
    EXPERIMENT_SE_WP_RESP_CONTAINER_LAYOUT,
  )
    ? options.componentType !== ComponentTypes.TpaWidget
    : true;
  const baseLayout = {
    type: 'SingleLayoutData',
    componentLayout: {
      type: 'ComponentLayout',
      height: options.height
        ? { type: 'px', value: options.height }
        : { type: 'auto' },
      width: {
        type: 'auto',
      },
    },
    itemLayout: {
      type: 'GridItemLayout',
      gridArea: {
        rowStart: 1,
        columnStart: 1,
        rowEnd: 2,
        columnEnd: 2,
      },
      alignSelf: 'stretch',
      justifySelf: 'stretch',
    },
  };

  return {
    ...baseLayout,
    ...addIf(shouldAddContainerLayout, {
      containerLayout: {
        type: 'GridContainerLayout',
        rows: [
          {
            type: 'fr',
            value: 1,
          },
        ],
        columns: [
          {
            type: 'fr',
            value: 1,
          },
        ],
      },
    }),
  };
}

const LEGACY_COMPONENT_TYPE_OVERRIDE_SUPPORTED_WIDGETS: SupportedWidgetType[] =
  [OOI_WIDGET, OOI_PAGE] as const;

const resolveComponentTypeOverride = (
  widgetPluginType: SupportedWidgetType,
  widgetAppComponent: AppDataComponent,
): string | undefined => {
  if (!isBuilderComponentModelSupported()) {
    return undefined;
  }

  if (widgetPluginType === BUILDER_WIDGET) {
    // @ts-expect-error TODO update dm types to include manifest later on when available
    return widgetAppComponent?.data?.type;
  }

  if (
    !LEGACY_COMPONENT_TYPE_OVERRIDE_SUPPORTED_WIDGETS.includes(widgetPluginType)
  ) {
    return undefined;
  }

  return (
    widgetAppComponent as unknown as { data?: { componentType?: string } }
  ).data?.componentType;
};

export async function createWidgetPluginComponentDefinition(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
  applicationId: number,
  experimentsAPI: IExperimentsApiAdapter,
  options: CreateWidgetPluginComponentDefinitionOptions = {},
): Promise<CompStructure> {
  const widgetAppComponent = getWidgetAppComponent(
    documentServicesAPI,
    widgetPointer,
  );

  const widgetPluginType = getWidgetPluginType(widgetAppComponent);

  const componentTypeOverride = resolveComponentTypeOverride(
    widgetPluginType,
    widgetAppComponent,
  );

  const widgetComponent = await compDefCreators[widgetPluginType](
    documentServicesAPI,
    widgetPointer,
    applicationId,
    widgetAppComponent as AppComponent,
    experimentsAPI,
    componentTypeOverride,
  );

  return applyWidgetPluginComponentDefinitionOptions(widgetComponent, options);
}

function applyWidgetPluginComponentDefinitionOptions(
  widgetComponent: CompStructure,
  options: CreateWidgetPluginComponentDefinitionOptions,
): CompStructure {
  if (!options.definitionOverrides) {
    return widgetComponent;
  }

  return options.definitionOverrides(widgetComponent);
}
